import { Banner, Button } from "@ui/react";
import { useAuth } from "../../lib/auth";
import { config } from "../../lib/config";
import { OnboardingLayout } from "./OnboardingLayout";

/**
 * Terminal state for an email whose `userType` claim is "homeowner".
 *
 * This screen is the whole reason `lib/auth.tsx` reads the claim instead of
 * inferring the gate from GET /profile's 403. Both Shoku apps share ONE
 * Firebase Auth pool, and the userType claim is single-valued — routing a
 * homeowner into /onboarding/profile and letting them submit it would
 * permanently rewrite their claim to "contractor", locking them out of every
 * homeowner-only route with no in-product way back (mirrors
 * shoku-user-website's WrongAccountPage for the reverse case).
 *
 * No "convert my account" affordance here on purpose — one email is one role.
 * Being terminal is exactly why it carries the address of the other door: the
 * homeowner site, per environment, rather than hard-coded — a dev tester sent
 * to the production site would sign in against a different Auth pool.
 */
export function WrongAccountScreen() {
  const { user, signOut } = useAuth();

  return (
    <OnboardingLayout
      eyebrow="Wrong app"
      title="That's a homeowner account."
      lead="A Shoku email address is either a homeowner or a contractor — never both. To take on contractor work, sign in with a different address or create a new account."
    >
      <div className="stack">
        <Banner tone="warn" title="That email is already registered">
          <b>{user?.email}</b> is registered as a Shoku homeowner, so it can't be used for
          contractor work. Sign in with a different email, or register a new contractor account.
        </Banner>

        {/* Not a router link: a different site, and the whole point is that
            this one has nothing more to offer this account. */}
        <a className="btn btn-primary btn-lg btn-block" href={config.userWebsite}>
          Go to Shoku for homeowners
        </a>

        <Button variant="secondary" size="lg" block onClick={() => void signOut()}>
          Sign out and use a different account
        </Button>
      </div>
    </OnboardingLayout>
  );
}
